import React, { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ShieldCheck, Loader2 } from "lucide-react";
import { useCart, formatEUR, SHIPPING_FREE_THRESHOLD_CENTS, STANDARD_SHIPPING_CENTS } from "../contexts/CartContext";
import { useLanguage } from "../i18n/LanguageContext";
import RedsysRedirectForm from "../components/checkout/RedsysRedirectForm";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CheckoutPage() {
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const {
    items,
    subtotalCents,
    shippingCents,
    totalCents,
    remainingForFreeCents,
    count,
    clear,
  } = useCart();
  const isFreeShipping = shippingCents === 0 && items.length > 0;

  const [form, setForm] = useState({
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    shipping_address: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [redirect, setRedirect] = useState(null);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (count === 0) {
      toast.error(t.checkout.empty);
      return;
    }
    if (!form.customer_name.trim() || !form.customer_email.trim() || !form.customer_phone.trim()) {
      toast.error(t.checkout.error);
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        items: items.map((it) => ({
          product_id: it.id,
          name: it.name,
          unit_price_cents: it.price_cents,
          quantity: it.quantity,
        })),
        language: lang,
      };
      const res = await axios.post(`${API}/checkout/create-order`, payload);
      if (res.status >= 200 && res.status < 300 && res.data?.redsys_url) {
        // Clear cart so coming back doesn't double-charge
        clear();
        setRedirect({
          action: res.data.redsys_url,
          fields: res.data.form_fields,
        });
      } else {
        toast.error(t.checkout.error);
      }
    } catch (err) {
      console.error(err);
      toast.error(t.checkout.error);
    } finally {
      setSubmitting(false);
    }
  };

  if (redirect) {
    return (
      <div
        data-testid="checkout-redirect"
        className="min-h-screen bg-[#F9F6F0] flex items-center justify-center px-6"
      >
        <div className="text-center">
          <Loader2 className="h-10 w-10 text-[#8B1E0F] animate-spin mx-auto mb-6" />
          <h1 className="font-serif-display text-3xl md:text-4xl text-[#1A1A1A]">
            {t.checkout.paying}
          </h1>
          <p className="mt-3 text-[#5C5C5C]">{t.checkout.legal}</p>
        </div>
        <RedsysRedirectForm action={redirect.action} fields={redirect.fields} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F6F0]" data-testid="checkout-page">
      <div className="max-w-6xl mx-auto px-6 md:px-10 lg:px-16 py-12 md:py-20">
        <button
          onClick={() => navigate("/")}
          data-testid="checkout-back"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-[#5C5C5C] hover:text-[#1A1A1A] mb-10"
        >
          <ArrowLeft className="h-4 w-4" />
          {t.checkout.back}
        </button>

        <div className="mb-10">
          <div className="eyebrow mb-3">{t.checkout.subtitle.split(".")[0]}.</div>
          <h1 className="font-serif-display text-4xl md:text-5xl lg:text-6xl tracking-tight text-[#1A1A1A] leading-[1.05]">
            {t.checkout.title}
          </h1>
          <p className="mt-4 text-[#5C5C5C] max-w-xl">{t.checkout.subtitle}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Form */}
          <form
            onSubmit={handleSubmit}
            data-testid="checkout-form"
            className="lg:col-span-7 space-y-10"
          >
            <Section title={t.checkout.contactSection}>
              <Field label={t.checkout.name}>
                <input
                  required
                  data-testid="checkout-name"
                  value={form.customer_name}
                  onChange={(e) => update("customer_name", e.target.value)}
                  className="input-underline"
                />
              </Field>
              <Field label={t.checkout.email}>
                <input
                  required
                  type="email"
                  data-testid="checkout-email"
                  value={form.customer_email}
                  onChange={(e) => update("customer_email", e.target.value)}
                  className="input-underline"
                />
              </Field>
              <Field label={t.checkout.phone}>
                <input
                  required
                  type="tel"
                  data-testid="checkout-phone"
                  value={form.customer_phone}
                  onChange={(e) => update("customer_phone", e.target.value)}
                  className="input-underline"
                />
              </Field>
            </Section>

            <Section title={t.checkout.shippingSection}>
              <Field label={t.checkout.address}>
                <textarea
                  rows={3}
                  data-testid="checkout-address"
                  value={form.shipping_address}
                  onChange={(e) => update("shipping_address", e.target.value)}
                  className="input-underline resize-none"
                />
              </Field>
              <Field label={t.checkout.notes}>
                <textarea
                  rows={2}
                  data-testid="checkout-notes"
                  value={form.notes}
                  onChange={(e) => update("notes", e.target.value)}
                  className="input-underline resize-none"
                />
              </Field>
            </Section>

            <div className="space-y-4">
              <p className="text-[10px] text-[#5C5C5C] leading-relaxed flex items-start gap-2">
                <ShieldCheck className="h-4 w-4 text-[#8B1E0F] mt-0.5 shrink-0" />
                {t.checkout.legal}
              </p>
              <button
                type="submit"
                disabled={submitting || count === 0}
                data-testid="checkout-pay"
                className="w-full md:w-auto inline-flex items-center justify-center gap-2 bg-[#8B1E0F] hover:bg-[#6A160A] disabled:opacity-60 text-white px-10 py-5 text-xs tracking-[0.2em] uppercase font-semibold transition-colors"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {t.checkout.paying}
                  </>
                ) : (
                  <>{t.checkout.pay} · {formatEUR(totalCents)}</>
                )}
              </button>
            </div>
          </form>

          {/* Summary */}
          <aside className="lg:col-span-5">
            <div className="bg-white border border-[#E5E0D8] p-6 md:p-8 lg:sticky lg:top-28">
              <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#5C5C5C] mb-6">
                {t.checkout.summary}
              </div>
              {items.length === 0 ? (
                <p className="text-[#5C5C5C] text-sm">{t.checkout.empty}</p>
              ) : (
                <ul className="divide-y divide-[#E5E0D8] mb-6">
                  {items.map((it) => (
                    <li
                      key={it.id}
                      data-testid={`summary-item-${it.id}`}
                      className="py-4 flex gap-3 items-start"
                    >
                      <img
                        src={it.image}
                        alt={it.name}
                        className="w-14 h-14 object-cover border border-[#E5E0D8]"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-serif-display text-base text-[#1A1A1A] leading-tight">
                          {it.name}
                        </div>
                        <div className="text-[10px] uppercase tracking-[0.18em] text-[#5C5C5C] mt-1">
                          {it.unit} · ×{it.quantity}
                        </div>
                      </div>
                      <div className="font-mono text-sm font-semibold text-[#1A1A1A] whitespace-nowrap">
                        {formatEUR(it.price_cents * it.quantity)}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              <div className="pt-4 border-t border-[#E5E0D8] space-y-2">
                <div className="flex items-baseline justify-between text-sm">
                  <span className="text-[#5C5C5C]">{t.cart.subtotal}</span>
                  <span data-testid="checkout-subtotal" className="font-mono text-[#1A1A1A]">
                    {formatEUR(subtotalCents)}
                  </span>
                </div>
                <div className="flex items-baseline justify-between text-sm">
                  <span className="text-[#5C5C5C]">{t.cart.shipping}</span>
                  <span
                    data-testid="checkout-shipping"
                    className={`font-mono ${
                      isFreeShipping
                        ? "text-[#1A8754] font-semibold"
                        : "text-[#1A1A1A]"
                    }`}
                  >
                    {isFreeShipping ? t.cart.shippingFree : formatEUR(shippingCents)}
                  </span>
                </div>
                {!isFreeShipping && count > 0 && (
                  <p className="text-[10px] text-[#8B1E0F] font-semibold uppercase tracking-[0.18em]">
                    {t.cart.freeShippingAt(formatEUR(remainingForFreeCents))}
                  </p>
                )}
                <div className="pt-3 mt-1 border-t border-[#1A1A1A]/15 flex items-baseline justify-between">
                  <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#5C5C5C]">
                    {t.checkout.total}
                  </span>
                  <span
                    data-testid="checkout-total"
                    className="font-serif-display text-3xl text-[#8B1E0F]"
                  >
                    {formatEUR(totalCents)}
                  </span>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h2 className="font-serif-display text-2xl md:text-3xl text-[#1A1A1A] mb-6 pb-2 border-b border-[#E5E0D8]">
        {title}
      </h2>
      <div className="space-y-5">{children}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[0.65rem] tracking-[0.2em] uppercase font-semibold text-[#5C5C5C] mb-1">
        {label}
      </div>
      {children}
    </label>
  );
}
